import './index.css'

import { datadogRum } from '@datadog/browser-rum'
import get from 'lodash/get'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Location, useLocation, useNavigate, useParams } from 'react-router-dom'

import api, { RemoteApi } from '@disco/api/server'
import {
  setDiscoPlaylist,
  setDiscoUser,
} from '@disco/state-management/actions/UploadActions'
import captureError from '@disco/utils/captureError'
import { validateLink } from '@disco/utils/links'
import { pushUINotification } from '@disco/utils/notifications'
import { getUrlHostname, getUrlParts } from '@disco/utils/url'

import type { DiscoPlaylist } from 'types/inbox'

import HelpTooltip from 'components/common/tooltip'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import DiscoModal from 'components/ui/disco-modal'
import FormNote from 'components/ui/form-note'
import Icon from 'components/ui/icon'
import TextField from 'components/ui/text-field'

import type { RootState } from 'store/configureStore'

const iconStyle = {
  marginBottom: '.1rem',
}
const PLAYLIST_MESSAGE =
  'Playlist added! Please click ‘Send files’ below to send your list.'
// Props type is defined above where connector is created

const UploadSelectPlaylist: React.FC<Props> = props => {
  const navigate = useNavigate()
  const [state, setState] = React.useState({
    url: get(props.discoPlaylist, 'url', ''),
    playlist: null as DiscoPlaylist | null | undefined,
    checkingPlaylistValidity: false,
    error: null as string | null | undefined,
  })

  const validate = (url?: string): { isValid: boolean; error?: string } => {
    if (!url || url.trim() === '') {
      return { isValid: false, error: 'The url field is required' }
    }

    if (!validateLink(url)) {
      return { isValid: false, error: 'The url field is invalid' }
    }

    return { isValid: true }
  }

  const _onClose = () => {
    const parts = getUrlParts(props.location.pathname)
    const path = parts.slice(0, -1).join('/')
    navigate(
      `/${path}/?${new URLSearchParams(props.location.search).toString()}`,
      { replace: true },
    )
  }

  const _setError = (error: string) =>
    setState(prevState => ({
      ...prevState,
      error,
      playlist: null,
      checkingPlaylistValidity: false,
    }))

  const _checkUrl = async (url: string): Promise<void> => {
    try {
      setState(prevState => ({
        ...prevState,
        checkingPlaylistValidity: true,
        error: null,
      }))
      const response = await api.getPlaylistFromPublicPage(url)
      const playlist =
        response.results && response.results.length > 0
          ? { ...response.results[0], url: url }
          : null

      if (playlist) {
        if (playlist.business.id === window.business_data.id) {
          return _setError(
            'Sorry! Playlists need to be from a different DISCO than this inbox.',
          )
        }

        if (!playlist.download_links) {
          return _setError(
            'Sorry! This playlist is streaming only. Please enable downloads for this playlist in your DISCO to share it.',
          )
        }
      }

      let errorMessage = playlist ? null : 'Playlist not found.'
      setState(prevState => ({
        ...prevState,
        playlist,
        checkingPlaylistValidity: false,
        error: errorMessage,
      }))
    } catch (error) {
      captureError(error)
      _setError('Playlist not found.')
    }
  }

  const _onUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value
    if (url === state.url && state.playlist) return
    setState(prevState => ({ ...prevState, url }))
    const validation = validate(url)
    if (validation.isValid) {
      _checkUrl(url)
    } else {
      setState(prevState => ({
        ...prevState,
        error: validation.error || null,
        playlist: null,
      }))
    }
  }

  const _onSubmit = async () => {
    const { discoUser } = props
    const { playlist } = state

    if (playlist) {
      props.setDiscoPlaylist(playlist)
      datadogRum.addAction(
        'Public inbox upload own playlist select attempted',
        {
          playlistId: playlist.id,
        },
      )

      // check if we can bypass contacting server is user already authenticated
      // (case where user changes url)
      if (
        discoUser &&
        getUrlHostname(playlist.url) === getUrlHostname(discoUser.url)
      ) {
        pushUINotification({
          message: PLAYLIST_MESSAGE,
          level: 'success',
        })
        return _onClose()
      }

      try {
        const senderApi = new RemoteApi()
        senderApi.host = `https://${playlist.business.hostname}`
        const user = await senderApi.getUserDetails()
        if (!user.id) throw new Error()
        props.setDiscoUser(user)
        pushUINotification({
          message: PLAYLIST_MESSAGE,
          level: 'success',
        })

        _onClose()
      } catch (error) {
        props.setDiscoUser(null)
        const parts = getUrlParts(props.location.pathname)
        const path = parts.slice(0, -1).join('/')
        navigate(
          `/${path}/auth/?${new URLSearchParams(
            props.location.search,
          ).toString()}`,
          { replace: true },
        )
      }
    }
  }

  return (
    <DiscoModal contentLabel="Select my playlist" onRequestClose={_onClose}>
      <div className="Form">
        <h1 className="Form_Header">
          Select my playlist
          <HelpTooltip id="tooltip_public_inbox_select_playlist">
            <p>Here you can share a playlist directly from your own DISCO.</p>
            <p>
              Once you’ve created the playlist, open the share pop up, copy the
              public playlist URL, and paste it in the field below.
            </p>
          </HelpTooltip>
        </h1>
        <div className="row">
          <div className="col-xs-12">
            <FormNote
              displayIcon={false}
              note="To send your own playlist, just copy the playlist share URL below"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            <TextField
              compact={false}
              label="Playlist URL"
              name="url"
              type="url"
              value={state.url}
              onChange={_onUrlChange}
              error={state.error}
            />
          </div>
        </div>
        {state.playlist && (
          <div className="row">
            <div className="col-xs-12">
              <div className="selected-playlist">
                <Icon type="check-circle" style={iconStyle} color="#5f63ee" />
                &nbsp; Playlist found: {state.playlist.name}
              </div>
            </div>
          </div>
        )}
        <div className="Form_ButtonsContainer separated-small">
          <ButtonContainer>
            <Button caption="Cancel" onClick={_onClose} />
            <Button
              appearance="primary"
              caption="Continue"
              disabled={!state.playlist}
              onClick={_onSubmit}
              progressCaption="Finding playlist"
              progressIndicator={state.checkingPlaylistValidity}
            />
          </ButtonContainer>
        </div>
      </div>
    </DiscoModal>
  )
}

const mapStateToProps = (state: RootState) => ({
  discoPlaylist: state.upload.discoPlaylist,
  discoUser: state.upload.discoUser,
})

const mapDispatchToProps = {
  setDiscoPlaylist,
  setDiscoUser,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  params: {
    groupId: string
    groupName: string
  }
  location: Location
}

const ConnectedComponent = connector(UploadSelectPlaylist)

function UploadSelectPlaylistWithRouter(props: any) {
  const location = useLocation()
  const params = useParams<{ groupId: string; groupName: string }>()

  return <ConnectedComponent {...props} location={location} params={params} />
}

export default UploadSelectPlaylistWithRouter
